import { AxiosError } from 'axios'
import axiosInstance from './AxiosInstance'

const LINK_STATUS_PATH = '/thiredparty/v1/link-status'

export type LinkStatusOutcome = 'pending' | 'expired' | 'active' | 'invalid'

export type LinkStatusResult =
  | { outcome: LinkStatusOutcome }
  | { outcome: 'error'; message: string }

/** Backend: { Status, Text, Result } — Text is the only field used for UI / routing logic */
export type LinkStatusApiBody = {
  Status?: string
  Text?: string
  Result?: number
}

/**
 * Read `Text` (PascalCase) or `text` from root or nested `data`.
 * Intentionally ignores `Status` for application logic.
 */
function pickText(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') {
    return undefined
  }
  const o = data as Record<string, unknown>
  const direct = o.Text ?? o.text
  if (typeof direct === 'string' && direct.length > 0) {
    return direct
  }
  if (o.data && typeof o.data === 'object') {
    const inner = o.data as Record<string, unknown>
    const nested = inner.Text ?? inner.text
    if (typeof nested === 'string' && nested.length > 0) {
      return nested
    }
  }
  return undefined
}

/**
 * Maps API `Text` to outcomes. Does not use `Status`.
 */
function outcomeFromText(textRaw: string): LinkStatusResult {
  const text = textRaw.trim()
  if (!text) {
    return { outcome: 'error', message: 'Empty response from server' }
  }

  const lower = text.toLowerCase()

  if (text === 'Invalid link' || lower === 'invalid link') {
    return { outcome: 'invalid' }
  }
  if (lower === 'pending') {
    return { outcome: 'pending' }
  }
  if (lower === 'expired') {
    return { outcome: 'expired' }
  }
  if (lower === 'active') {
    return { outcome: 'active' }
  }

  return { outcome: 'error', message: text }
}

/**
 * Public link status check (used by Live Job Tracking).
 * POST /thiredparty/v1/link-status — base URL from REACT_APP_API_URL.
 */
export async function postLinkStatus(jobKey: string): Promise<LinkStatusResult> {
  const trimmed = jobKey.trim()
  if (!trimmed) {
    return { outcome: 'error', message: 'Missing job key' }
  }

  try {
    const { data } = await axiosInstance.post<unknown>(LINK_STATUS_PATH, { jobKey: trimmed })
    const text = pickText(data)
    if (text === undefined) {
      return { outcome: 'error', message: 'Invalid response from server' }
    }
    return outcomeFromText(text)
  } catch (err) {
    const ax = err as AxiosError<LinkStatusApiBody & { message?: string; error?: string }>
    const body = ax.response?.data

    const textFromError = body && pickText(body)
    if (textFromError) {
      const fromText = outcomeFromText(textFromError)
      if (fromText.outcome !== 'error') {
        return fromText
      }
    }

    const msgFromBody =
      (body && typeof body === 'object' && typeof body.message === 'string' && body.message) ||
      (body && typeof body === 'object' && typeof (body as { error?: string }).error === 'string' &&
        (body as { error: string }).error) ||
      undefined

    const fallback =
      msgFromBody ||
      ax.message ||
      'Unable to verify link. Please try again.'
    return { outcome: 'error', message: fallback }
  }
}
